import React from 'react';
import {
	FormattedMessage,
	defineMessages,
} from 'react-intl';

import ArrayGroupField, {
	type ArrayGroupFieldRendererProps,
	type ArrayGroupFieldValidationInput,
} from '~/components/patterns/forms/fields/ArrayGroupField';
import ExpressionField from './ExpressionField';
import MethodSelectionField from './MethodSelectionField';
import OccurrenceSelectionField from './OccurrenceSelectionField';

import {
	EXTRACTION_METHOD_CSS,
	EXTRACTION_METHOD_REGEXP,
	EXTRACTION_METHOD_XPATH,
	OCCURRENCES_ALL,
	validateExpressionOfCss,
	validateExpressionOfRegexp,
	validateExpressionOfXPath,
} from '~/model/customElements';

import getArrayItemAtSafeIndex from '~/utilities/getArrayItemAtSafeIndex';
import {
	isNonEmptyString,
} from '~/utilities/typeCheck';



const messages = defineMessages({
	addButton: {
		id: 'ui.customElements.extractionSteps.actions.addAnotherStep',
	},
	columnExpression: {
		id: 'ui.customElements.extractionSteps.header.expression.label',
	},
	columnMethod: {
		id: 'ui.customElements.extractionSteps.header.method',
	},
	columnOccurrence: {
		id: 'ui.customElements.extractionSteps.header.occurrence',
	},
	columnStep: {
		id: 'ui.customElements.extractionSteps.header.step',
	},
	deleteStepButtonLabel: {
		id: 'ui.customElements.extractionSteps.actions.deleteStep.button',
	},
	formErrorsBlank: {
		id: 'ui.formErrors.blank',
	},
	formErrorsInvalidCss: {
		id: 'ui.customElements.formErrors.invalidCss',
	},
	formErrorsInvalidRegexp: {
		id: 'ui.customElements.formErrors.invalidRegexp',
	},
	formErrorsInvalidXPath: {
		id: 'ui.customElements.formErrors.invalidXPath',
	},
	formErrorsNotRegexpAfterRegexp: {
		id: 'ui.customElements.formErrors.notRegexpAfterRegexp',
	},
	tooMany: {
		id: 'ui.customElements.extractionSteps.tooMany',
	},
});



type Props = {
	name: string,
	showAddButton: boolean,
};

const ExtractionStepsConfigurator: React.FC<Props> = (props) => {
	const {
		name,
		showAddButton,
	} = props;

	// <TableDeleteButton
	// 	confirmation={!!(values[ruleTypeFieldName] || values[ruleExpressionFieldName]) ? {
	// 		buttonLabel: (
	// 			<FormattedMessage {...messages.deleteStepButtonLabel} />
	// 		),
	// 	} : undefined}
	// 	onClick={onRemoveStepCallback.bind(this, step)}
	// />

	const fields = React.useMemo(
		() => {
			return [
				{
					label: (
						<FormattedMessage {...messages.columnMethod} />
					),
					name: 'method',
					renderer: ({ fieldName }: ArrayGroupFieldRendererProps) => (
						<MethodSelectionField
							name={fieldName}
							width="100%"
						/>
					),
					validation: ({ f, getValueInRow, listRows, rowIndex }: ArrayGroupFieldValidationInput) => ([
						f.when(({ values }) => {
							return isNonEmptyString(getValueInRow(values, 'expression'));
						}),
						f.validateNonEmpty(),
						f.custom({
							message: (
								<FormattedMessage {...messages.formErrorsNotRegexpAfterRegexp} />
							),
							rule: ({ value, values }) => {
								const method = value;

								if (method) {
									let precededByRegexp = false;
									const rows = listRows(values);

									for (let index = 0; index < rows.length; index++) {
										if (index === rowIndex) {
											if (precededByRegexp && method !== EXTRACTION_METHOD_REGEXP) {
												return false;
											}

											return true;
										}

										const otherStepMethod = getArrayItemAtSafeIndex(rows, index).method;

										if (precededByRegexp && otherStepMethod !== EXTRACTION_METHOD_REGEXP) {
											return true;
										}

										if (otherStepMethod === EXTRACTION_METHOD_REGEXP) {
											precededByRegexp = true;
										}
									}
								}

								return true;
							},
						}),
					]),
					width: '30%',
				},
				{
					label: (
						<FormattedMessage {...messages.columnExpression} />
					),
					name: 'expression',
					renderer: ({ fieldName }: ArrayGroupFieldRendererProps) => (
						<ExpressionField
							name={fieldName}
							width="100%"
						/>
					),
					validation: ({ f, getValueInRow }: ArrayGroupFieldValidationInput) => ([
						f.when(({ values }) => {
							return isNonEmptyString(getValueInRow(values, 'method'));
						}),
						f.validateNonEmpty(),
						f.custom({
							message: (
								<FormattedMessage {...messages.formErrorsInvalidCss} />
							),
							rule: ({ value, values }) => {
								if (getValueInRow(values, 'method') !== EXTRACTION_METHOD_CSS) {
									return true;
								}

								return validateExpressionOfCss(value);
							},
						}),
						f.custom({
							message: (
								<FormattedMessage {...messages.formErrorsInvalidRegexp} />
							),
							rule: ({ value, values }) => {
								if (getValueInRow(values, 'method') !== EXTRACTION_METHOD_REGEXP) {
									return true;
								}

								return validateExpressionOfRegexp(value);
							},
						}),
						f.custom({
							message: (
								<FormattedMessage {...messages.formErrorsInvalidXPath} />
							),
							rule: ({ value, values }) => {
								if (getValueInRow(values, 'method') !== EXTRACTION_METHOD_XPATH) {
									return true;
								}

								return validateExpressionOfXPath(value);
							},
						}),
					]),
					width: 'auto',
				},
				{
					defaultValue: OCCURRENCES_ALL,
					label: (
						<FormattedMessage {...messages.columnOccurrence} />
					),
					name: 'occurrence',
					renderer: ({ fieldName }: ArrayGroupFieldRendererProps) => (
						<OccurrenceSelectionField
							name={fieldName}
							width={104}
						/>
					),
					width: 120,
				},
			];
		},
		[],
	);

	return (
		<ArrayGroupField
			addButtonLabel={(
				<FormattedMessage {...messages.addButton} />
			)}
			fields={fields}
			isSortable={true}
			maximumRows={6}
			minimumRows={1}
			name={name}
			showAddButton={showAddButton}
			showRowNumbers={true}
		/>
	);
};



export default ExtractionStepsConfigurator;
